import { useState, useEffect, useRef } from "react";
import { Archive, ArchiveRestore, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DataTable, DataTableColumn } from "@/components/ui/data-table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useAgentRulesStore } from "@/store/agentRulesStore";
import { useEntitySearch } from "@/lib/useEntitySearch";
import { useAuthStore, useHasPermission } from "@/store/authStore";
import {
  AgentRule,
  AGENT_RULE_ACTOR_TYPE,
  Permission,
} from "@forgepoint/shared";
import { toast } from "sonner";
import AgentRuleEditorSheet from "./AgentRuleEditorSheet";

/**
 * Controller for managing agent rules/shop rules.
 * Displays a list of all agent rules in a table format with create, edit, archive, and search functionality.
 * Each rule shows its content and provides edit/archive actions.
 * Supports full text search to filter rules by content.
 * Allows creating new shop rules and editing existing ones through a sheet interface.
 */
export default function AgentRulesController() {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | undefined>();
  const [ruleToArchive, setRuleToArchive] = useState<AgentRule | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const previousRulesSize = useRef<number>(0);

  const {
    rules,
    listLoading,
    getRuleLoadingState,
    fetchRules,
    createRule,
    updateRule,
    setRuleArchived,
  } = useAgentRulesStore();
  const user = useAuthStore((state) => state.user);
  const canEdit = useHasPermission(Permission.SHOPRULES_EDIT);

  const { entitiesArray, setDisplayedEntityIds, handleSearch } =
    useEntitySearch<AgentRule>({
      entities: rules,
      fetchEntities: (params) => fetchRules(params?.search, showArchived),
    });

  useEffect(() => {
    const currentSize = rules.size;
    if (
      previousRulesSize.current > 0 &&
      currentSize > previousRulesSize.current
    ) {
      fetchRules(undefined, showArchived).then((ids) =>
        setDisplayedEntityIds(ids),
      );
    }
    previousRulesSize.current = currentSize;
  }, [rules.size, fetchRules, setDisplayedEntityIds, showArchived]);

  const handleCreateRule = () => {
    setShowCreateDialog(true);
  };

  const handleEditRule = (ruleId: string) => {
    setEditingRuleId(ruleId);
  };

  const handleRuleSaved = (rule: AgentRule) => {
    setDisplayedEntityIds((prev) => {
      if (prev.includes(rule.id)) {
        return prev;
      }
      return [rule.id, ...prev];
    });
    setShowCreateDialog(false);
    setEditingRuleId(undefined);
  };

  const handleSaveCreate = async (content: string) => {
    if (!user) {
      toast.error("User not authenticated");
      return false;
    }

    const result = await createRule(
      content,
      user.id,
      AGENT_RULE_ACTOR_TYPE.USER,
    );

    if (result) {
      handleRuleSaved(result);
      return true;
    }
    return false;
  };

  const handleSaveEdit = async (content: string) => {
    if (!editingRuleId) {
      return false;
    }
    const result = await updateRule(editingRuleId, content);
    if (result) {
      handleRuleSaved(result);
      return true;
    }
    return false;
  };

  const handleToggleArchived = (checked: boolean) => {
    setShowArchived(checked);
    fetchRules(undefined, checked).then(setDisplayedEntityIds);
  };

  const handleConfirmArchive = async () => {
    if (!ruleToArchive) {
      return;
    }

    const ruleId = ruleToArchive.id;
    const success = await setRuleArchived(ruleId, true);
    if (success) {
      if (!showArchived) {
        setDisplayedEntityIds((prev) => prev.filter((id) => id !== ruleId));
      }
      toast.success("Shop rule archived");
    } else {
      toast.error("Failed to archive shop rule");
    }
    setRuleToArchive(null);
  };

  const handleUnarchive = async (ruleId: string) => {
    const success = await setRuleArchived(ruleId, false);
    if (success) {
      toast.success("Shop rule unarchived");
    } else {
      toast.error("Failed to unarchive shop rule");
    }
  };

  const columns: DataTableColumn<AgentRule>[] = [
    {
      key: "content",
      header: "Shop Rule Content",
      type: "string",
      className: "break-all",
      format: (_value: unknown, rule: AgentRule) => (
        <div className="line-clamp-2">{rule.content}</div>
      ),
    },
    {
      key: "actorName",
      header: "Added By",
      width: 160,
      format: (_value: unknown, rule: AgentRule) => rule.actorName ?? "—",
    },
    {
      key: "createdAt",
      header: "Created",
      type: "date",
      width: 120,
    },
    {
      key: "updatedAt",
      header: "Updated",
      type: "date",
      width: 120,
    },
    ...(canEdit
      ? [
          {
            key: "actions",
            header: "",
            width: 60,
            format: (_value: unknown, rule: AgentRule) => {
              const loadingState = getRuleLoadingState(rule.id);
              const isArchiving = loadingState.isLoadingArchive;
              const isArchived = !!rule.archivedAt;

              return (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isArchived) {
                      handleUnarchive(rule.id);
                    } else {
                      setRuleToArchive(rule);
                    }
                  }}
                  disabled={isArchiving}
                  aria-label={
                    isArchived ? "Unarchive shop rule" : "Archive shop rule"
                  }
                >
                  {isArchiving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isArchived ? (
                    <ArchiveRestore className="h-4 w-4" />
                  ) : (
                    <Archive className="h-4 w-4" />
                  )}
                </Button>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <div className="flex flex-col h-full">
        <DataTable
          columns={columns}
          data={entitiesArray}
          searchPlaceholder="Search shop rules..."
          onChangeSearch={handleSearch}
          className="h-full"
          isLoading={listLoading && entitiesArray.length === 0}
          onCreate={canEdit ? handleCreateRule : undefined}
          createButtonText="New Shop Rule"
          onClickRow={canEdit ? (row) => handleEditRule(row.id) : undefined}
          toolbarActions={
            <div className="flex items-center gap-2">
              <Switch
                id="show-archived-shop-rules"
                checked={showArchived}
                onCheckedChange={handleToggleArchived}
              />
              <Label
                htmlFor="show-archived-shop-rules"
                className="text-sm whitespace-nowrap"
              >
                Show Archived
              </Label>
            </div>
          }
        />
      </div>

      {/* Create Shop Rule Sheet */}
      <AgentRuleEditorSheet
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSave={handleSaveCreate}
        mode="create"
      />

      {/* Edit Shop Rule Sheet */}
      {editingRuleId && (
        <AgentRuleEditorSheet
          open={!!editingRuleId}
          onOpenChange={(open) => !open && setEditingRuleId(undefined)}
          ruleId={editingRuleId}
          onSave={handleSaveEdit}
          mode="edit"
        />
      )}

      <AlertDialog
        open={!!ruleToArchive}
        onOpenChange={(open) => !open && setRuleToArchive(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Shop Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive this shop rule? Archived rules
              are excluded from agent instructions. You can unarchive it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmArchive}>
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
